import { useContext, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import {
  buildMetaUserData,
  trackEvent,
  trackPageView as trackPageViewEvent,
} from 'lib/analytics';
import { useReportWebVitals } from 'next/web-vitals';
import analytics, { loadAnalytics } from 'lib/segment';
import { UserContext } from 'context-providers/user-provider';
import { getOptimizelyEdgeExperiments } from 'lib/utils/get-optimizely-edge-experiments';
import { trackFeatureFlag } from 'lib/datadog-rum';
import { EXPERIMENT_ROUTING_CONFIG } from 'hooks/use-experiment-routing';
import useShopifyLoginAnalytics from 'hooks/use-shopify-login-analytics';
import { useDigiohWinback } from 'hooks/use-digioh-winback';
import { storeUtmParameters } from 'lib/utils/store-utm-parameters';
import { LANDER_CART_PATH } from 'lib/lp/lander-cart-protocol';

const MAX_WAIT_TIME = 300;

// Experiment names to track - add to list if writing cloudflare worker experiment
// that does not add to experiment routing config
const EXPERIMENTS_TO_TRACK = [
  ...Object.keys(EXPERIMENT_ROUTING_CONFIG),
  'cf_ds01_pdp_jan2026_v1',
  'cf_ds_quiz_pdp_feb_2026_v2',
  'cf_homepage_benefits_nov2026',
];

const Analytics = () => {
  const router = useRouter();
  const { userState } = useContext(UserContext);
  const loggedMetrics = useRef(new Map<string, number>());
  const pageCounter = useRef(1);
  const initialPageTracked = useRef(false);
  const userStateRef = useRef(userState);
  userStateRef.current = userState;
  // Draft mode is the Sanity preview, which refreshes on every edit, so its page
  // views aren't visits. A ref because Next sets isPreview per route.
  const isPreviewRef = useRef(router.isPreview);
  isPreviewRef.current = router.isPreview;

  // Fire Login + Checkout Step Completed events after Shopify OAuth redirect
  useShopifyLoginAnalytics();

  useDigiohWinback();

  const waitForMetrics = (metricNames: string[]) => {
    return new Promise<void>((resolve) => {
      const interval = setInterval(() => {
        const allMetricsAvailable = metricNames.every((name) =>
          loggedMetrics.current.has(name),
        );
        if (allMetricsAvailable) {
          clearInterval(interval);
          resolve();
        }
      }, 100);

      setTimeout(() => {
        clearInterval(interval);
        resolve();
      }, MAX_WAIT_TIME);
    });
  };

  // Wait briefly for userState to hydrate (auth cookie -> apiUserGet) so the
  // initial page view can include Meta CAPI userData. Capped at MAX_WAIT_TIME
  // and runs in parallel with waitForMetrics, so no extra latency for anon
  // users who never have userState.
  const waitForUserState = () => {
    return new Promise<void>((resolve) => {
      if (userStateRef.current?.email) return resolve();
      const interval = setInterval(() => {
        if (userStateRef.current?.email) {
          clearInterval(interval);
          resolve();
        }
      }, 50);
      setTimeout(() => {
        clearInterval(interval);
        resolve();
      }, MAX_WAIT_TIME);
    });
  };

  useReportWebVitals((metric) => {
    if (!loggedMetrics.current.has(metric.name)) {
      loggedMetrics.current.set(metric.name, metric.value);
    }
  });

  useEffect(() => {
    // Store UTM parameters immediately
    storeUtmParameters();

    // Load Segment with OneTrust wrapper
    loadAnalytics();

    // Track initial page view once analytics is ready
    analytics.ready(() => {
      if (initialPageTracked.current) return;
      initialPageTracked.current = true;

      // only Landerjams use this path in an iframe, so don't track page view for the cart
      if (window.location.pathname === LANDER_CART_PATH) return;

      // Track Optimizely experiments after Segment is ready
      // OneTrust wrapper auto-detects consent model based on user's geolocation
      const optimizelyEdgeExperiments = getOptimizelyEdgeExperiments();
      if (optimizelyEdgeExperiments) {
        Object.entries(optimizelyEdgeExperiments).forEach(
          ([experimentName, variationName]) => {
            if (EXPERIMENTS_TO_TRACK.includes(experimentName)) {
              trackEvent('Experiment Viewed', {
                experimentName,
                variationName,
              });
              if (typeof variationName === 'string') {
                trackFeatureFlag(experimentName, variationName);
              }
            }
          },
        );
      }

      if (isPreviewRef.current) return;

      // Wait for web vitals metrics + userState hydration before tracking page
      // Browser API support for LCP is not available in Safari
      // https://github.com/GoogleChrome/web-vitals?tab=readme-ov-file#browser-support
      Promise.all([waitForMetrics(['FCP', 'TTFB']), waitForUserState()]).then(
        async () => {
          const pageProperties: Record<string, number | string | object> = {
            fcp: loggedMetrics.current.get('FCP') || 0,
            ttfb: loggedMetrics.current.get('TTFB') || 0,
            // lcp: loggedMetrics.current.get('LCP')!,
            pageCounter: pageCounter.current,
          };

          const userData = await buildMetaUserData(userStateRef.current);
          if (userData) pageProperties.userData = userData;

          trackPageViewEvent(window.location.pathname, pageProperties);
          pageCounter.current += 1;
        },
      );
    });
  }, []);

  useEffect(() => {
    const trackPageView = async (url: string) => {
      const userData = await buildMetaUserData(userStateRef.current);
      trackPageViewEvent(window.location.pathname, {
        path: window.location.pathname,
        url: `${window.location.origin}${url}`,
        title: document.title,
        pageCounter: pageCounter.current,
        ...(userData ? { userData } : {}),
      });
      pageCounter.current += 1;
    };

    const handleRouteChangeComplete = (url: string) => {
      if (isPreviewRef.current) return;
      setTimeout(() => {
        trackPageView(url);
      }, 1);
    };

    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router.events]);

  return null;
};

export default Analytics;
